import { Box } from '@mui/material';
import styled from 'styled-components';

export const MarkdownReaderContainer = styled(Box)`
  && {
    width: 100%;
    height: 100%;
    overflow: auto;
    color: ${({ theme }) => theme.palette.text.primary};

    /* Remove margin from first and last children for clean edges */
    & > *:first-child {
      margin-top: 0;
    }

    & > *:last-child {
      margin-bottom: 0;
    }

    /* Headings */
    h1 {
      ${({ theme }) => theme.typography.h5};
      margin: 1.5rem 0 0.75rem;
    }

    h2 {
      ${({ theme }) => theme.typography.h6};
      margin: 1.25rem 0 0.625rem;
    }

    h3 {
      ${({ theme }) => theme.typography.p1};
      margin: 1rem 0 0.5rem;
    }

    h4 {
      ${({ theme }) => theme.typography.p4};
      margin: 1rem 0 0.5rem;
    }

    h5,
    h6 {
      ${({ theme }) => theme.typography.p6};
      margin: 0.75rem 0 0.375rem;
    }

    h6 {
      color: ${({ theme }) => theme.palette.text.secondary};
    }

    /* Paragraphs */
    p {
      ${({ theme }) => theme.typography.p5};
      margin: 0.625rem 0;
    }

    /* Bold text */
    strong {
      font-weight: ${({ theme }) => theme.typography.fontWeightBold};
    }

    /* Emphasis text */
    em {
      font-style: italic;
    }

    /* Links */
    a {
      color: ${({ theme }) => theme.palette.primary.main};
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }

    /* Lists */
    ul,
    ol {
      padding-left: 1.5rem;
      margin: 0.625rem 0;
    }

    ul {
      list-style-type: disc;
    }

    ol {
      list-style-type: decimal;
    }

    li {
      ${({ theme }) => theme.typography.p5};
      margin: 0.25rem 0;

      & > ul,
      & > ol {
        margin: 0.125rem 0;
      }
    }

    /* Code blocks */
    pre {
      background-color: ${({ theme }) => theme.palette.background.default};
      border: 1px solid ${({ theme }) => theme.palette.divider};
      border-radius: ${({ theme }) => theme.shape.containerRadius};
      padding: 0.875rem 1rem;
      margin: 0.75rem 0;
      overflow-x: auto;

      code {
        background: none;
        padding: 0;
        border: none;
        ${({ theme }) => theme.typography.p8};
        font-family: 'Courier New', Courier, monospace;
      }
    }

    /* Inline code */
    :not(pre) > code {
      ${({ theme }) => theme.typography.p8};
      font-family: 'Courier New', Courier, monospace;
      background-color: ${({ theme }) => theme.palette.background.default};
      border: 1px solid ${({ theme }) => theme.palette.divider};
      border-radius: 4px;
      padding: 0.125rem 0.375rem;
    }

    /* Blockquotes */
    blockquote {
      border-left: 3px solid ${({ theme }) => theme.palette.primary.main};
      padding: 0.25rem 0 0.25rem 1rem;
      margin: 0.75rem 0;
      color: ${({ theme }) => theme.palette.text.secondary};

      p {
        margin: 0.25rem 0;
      }
    }

    /* Horizontal rule */
    hr {
      border: none;
      border-top: 1px solid ${({ theme }) => theme.palette.divider};
      margin: 1.25rem 0;
    }

    /* Tables */
    table {
      border-spacing: 0;
      width: 100%;
      margin: 0.75rem 0;

      td,
      th {
        text-align: left;
        padding: 0.625rem 1rem;
        border-top: 1px solid ${({ theme }) => theme.palette.divider};
        border-left: 1px solid ${({ theme }) => theme.palette.divider};

        &:last-of-type {
          border-right: 1px solid ${({ theme }) => theme.palette.divider};
        }
      }

      tr:first-of-type {
        th {
          &:first-of-type {
            border-top-left-radius: ${({ theme }) =>
              theme.shape.containerRadius};
          }

          &:last-of-type {
            border-top-right-radius: ${({ theme }) =>
              theme.shape.containerRadius};
          }
        }
      }

      tr:last-of-type {
        td {
          border-bottom: 1px solid ${({ theme }) => theme.palette.divider};

          &:first-of-type {
            border-bottom-left-radius: ${({ theme }) =>
              theme.shape.containerRadius};
          }

          &:last-of-type {
            border-bottom-right-radius: ${({ theme }) =>
              theme.shape.containerRadius};
          }
        }
      }

      th {
        background-color: ${({ theme }) => theme.palette.background.default};
        ${({ theme }) => theme.typography.p6};
      }

      td {
        ${({ theme }) => theme.typography.p8};
      }
    }

    /* Images */
    img {
      max-width: 100%;
      border-radius: ${({ theme }) => theme.shape.containerRadius};
    }
  }
`;
